import React, { useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { useTaskService } from '../../../contexts/TaskServiceContext';
import {
	AREA_ORDER,
	areaOrderFor,
	areaMeta,
	PRIORITY_ORDER,
	STATUS_ORDER,
	STATUS_META,
	GATE_ORDER,
	gateMeta,
	PEOPLE_BY_UID,
} from './taskModel';
import { accentStyle } from './TaskBits';
import { Avatar } from './Avatar';
import { STUDIO_FIELDS, isPersonOffered, isGateOffered } from './spaceFields';

const EFFORTS = ['small', 'medium', 'large'];
const PEOPLE = Object.values(PEOPLE_BY_UID);

function toDateInput(d) {
	if (!d) return '';
	const date = d instanceof Date ? d : new Date(d);
	if (Number.isNaN(date.getTime())) return '';
	const m = `${date.getMonth() + 1}`.padStart(2, '0');
	const day = `${date.getDate()}`.padStart(2, '0');
	return `${date.getFullYear()}-${m}-${day}`;
}
function fromDateInput(s) {
	if (!s) return null;
	const [y, m, d] = s.split('-').map(Number);
	return new Date(y, m - 1, d);
}

// Portal Phase C — create / edit form (C3). One component backs the mobile
// quick-add bottom sheet (collapsed: title + smart chips + "More options") and
// the full create/edit form (expanded). The SAME draft carries between them, so
// "More options" never loses typing. Editing pre-fills from `initial`.
function TaskForm({
	initial,
	parentId,
	defaultProjectId,
	projects = [],
	// Which studio fields this space offers (see spaceFields.js). Defaults to the
	// full set, so a mount outside a private space is unchanged.
	fields = STUDIO_FIELDS,
	onClose,
	onSaved,
}) {
	const { currentUser, userProfile } = useAuth();
	// Which space's collection this form writes to. Defaults to Work when no
	// provider wraps the tree, so every existing mount is unaffected.
	const { createTask, updateTask, deleteTask } = useTaskService();
	const isAdmin =
		userProfile?.role === 'admin' || userProfile?.owner === true;
	const editing = !!initial?.id;
	const [expanded, setExpanded] = useState(editing);
	const [saving, setSaving] = useState(false);
	// Hard-delete affordance (admin-only, existing tasks only). A single inline
	// confirm naming the task — the live teacherTasks subscription drops the row,
	// so we just close the form; nothing here re-reads the deleted doc.
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const [deleting, setDeleting] = useState(false);

	const [title, setTitle] = useState(initial?.title || '');
	const [description, setDescription] = useState(initial?.description || '');
	const [area, setArea] = useState(initial?.area || '');
	const [assignees, setAssignees] = useState(
		() => new Set(initial?.assigneeUids || [])
	);
	const [priority, setPriority] = useState(initial?.priority || 'medium');
	const [status, setStatus] = useState(initial?.status || 'pending');
	const [effort, setEffort] = useState(initial?.effort || '');
	/* The offered sets, computed ONCE and shared by both layouts (the compact
	   smart-chip row and FullFields) — the two used to read module-level PEOPLE
	   independently, which is exactly how one of them gets narrowed and the other
	   does not.

	   A VALUE ALREADY ON THE TASK IS ALWAYS INCLUDED, even when the space would
	   not offer it today. A task assigned to Annelise, or parked on `cowork`,
	   predates this narrowing or arrived by import; dropping it from the picker
	   would render the chip un-toggleable and the select blank, so opening the
	   form and saving would quietly erase a real value. Narrowing what can be
	   CHOSEN must never narrow what can be SEEN or UNDONE. */
	const offeredPeople = useMemo(() => {
		const assigned = new Set(initial?.assigneeUids || []);
		return PEOPLE.filter((p) => isPersonOffered(fields, p) || assigned.has(p.uid));
	}, [fields, initial]);
	const offeredGates = useMemo(() => {
		const current = initial?.gate || '';
		return GATE_ORDER.filter((g) => isGateOffered(fields, g) || g === current);
	}, [fields, initial]);
	/* Same already-set-value clause as people and gates, and it is NOT theoretical
	   here: every task in the private spaces predates their own area vocabulary,
	   and a task carrying a studio area would otherwise render a blank select —
	   so opening the form and pressing save would quietly erase it. Appended
	   rather than filtered in, because a private space's list is a substitution:
	   the stale key is not in it to be kept. */
	const offeredAreas = useMemo(() => {
		const base = areaOrderFor(fields);
		const current = initial?.area || '';
		return current && !base.includes(current) ? [...base, current] : base;
	}, [fields, initial]);

	const [projectId, setProjectId] = useState(
		initial?.projectId || defaultProjectId || ''
	);
	const [due, setDue] = useState(toDateInput(initial?.deadline));
	const [gate, setGate] = useState(initial?.gate || '');
	const [subtasks, setSubtasks] = useState([]); // new children (create flow)
	const [subInput, setSubInput] = useState('');

	const toggleAssignee = (uid) =>
		setAssignees((prev) => {
			const next = new Set(prev);
			if (next.has(uid)) next.delete(uid);
			else next.add(uid);
			return next;
		});

	const buildPayload = () => {
		const uids = [...assignees];
		const names = uids.map((u) => PEOPLE_BY_UID[u]?.name).filter(Boolean);
		return {
			title: title.trim(),
			description,
			area: area || null,
			assigneeUids: uids,
			assignedTo: names,
			priority,
			effort: effort || null,
			projectId: projectId || null,
			deadline: fromDateInput(due),
			gate: gate || null,
			// Status is editable only from the edit form (existing tasks); new
			// tasks keep createTask's default so we never stamp a status on create.
			...(editing ? { status } : {}),
		};
	};

	const handleSave = async () => {
		if (!title.trim() || saving) return;
		setSaving(true);
		try {
			const payload = buildPayload();
			if (editing) {
				await updateTask(initial.id, payload);
			} else {
				// A non-admin only sees tasks she's assigned to (the E1 read rule +
				// the assignee-scoped teacher-dashboard subscription). If she creates
				// one without picking anyone it lands unassigned and vanishes from her
				// own board with no feedback (#29) — default her in. Admins keep the
				// deliberate "unassigned → triage pool" behavior (they see all tasks).
				// notifyTaskAssigned already skips the creator, so this self-assign
				// produces no self-push.
				if (!isAdmin && payload.assigneeUids.length === 0 && currentUser?.uid) {
					payload.assigneeUids = [currentUser.uid];
					const selfName =
						PEOPLE_BY_UID[currentUser.uid]?.name ||
						userProfile?.displayName ||
						null;
					payload.assignedTo = selfName ? [selfName] : [];
				}
				const newId = await createTask({
					...payload,
					parentId: parentId || null,
					source: 'manual',
					createdBy: currentUser?.uid || null,
					createdByName:
						userProfile?.displayName ||
						currentUser?.displayName ||
						currentUser?.email ||
						null,
				});
				// Create any sub-tasks as top-level docs carrying parentId. They
				// inherit the parent's assignees + project so the creator can see
				// them too (a bare subtask with no assigneeUids is invisible to an
				// assignee-scoped teacher — the other half of #29).
				for (const st of subtasks) {
					if (!st.trim()) continue;
					await createTask({
						title: st.trim(),
						area: payload.area,
						priority: 'medium',
						source: 'manual',
						parentId: newId,
						assigneeUids: payload.assigneeUids,
						assignedTo: payload.assignedTo,
						projectId: payload.projectId,
						createdBy: currentUser?.uid || null,
						createdByName:
							userProfile?.displayName ||
							currentUser?.displayName ||
							currentUser?.email ||
							null,
					});
				}
			}
			onSaved?.();
			onClose?.();
		} catch (e) {
			console.error('Failed to save task:', e);
			setSaving(false);
		}
	};

	// Admin-only hard delete of an existing task (rule: allow delete: if isAdmin()).
	// No cascade — orphaned subtasks/comments are acceptable for junk cleanup in v1
	// (follow-up if it ever matters). onClose after the write; the parent's live
	// onSnapshot removes the row from the queue.
	const handleDelete = async () => {
		if (!editing || deleting) return;
		setDeleting(true);
		try {
			await deleteTask(initial.id);
			onClose?.();
		} catch (e) {
			console.error('Failed to delete task:', e);
			setDeleting(false);
			setConfirmingDelete(false);
		}
	};

	const eyebrow = editing
		? 'Edit task'
		: parentId
		? 'New sub-task'
		: 'New task';

	return (
		<div className="wt-overlay" onClick={onClose}>
			<div
				className="wt-sheet wt-sheet--wide"
				onClick={(e) => e.stopPropagation()}
				style={accentStyle(area)}>
				<div className="wt-grabhandle" />
				<div className="wt-sheet__bar">
					<span className="wt-sheet__eyebrow">{eyebrow}</span>
					<button
						type="button"
						className="wt-iconbtn"
						aria-label="Close"
						onClick={onClose}>
						<i className="fa-sharp fa-solid fa-xmark" />
					</button>
				</div>

				<div className="wt-sheet__body">
					{/* eslint-disable-next-line jsx-a11y/no-autofocus */}
					<input
						className="wt-input wt-input--title"
						autoFocus
						value={title}
						placeholder="What needs doing?"
						onChange={(e) => setTitle(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !expanded) handleSave();
						}}
					/>

					{!expanded ? (
						<>
							{/* Smart chips — set the essentials without leaving */}
							<div className="wt-smartchips">
								<label className="wt-smartchip">
									<i className="fa-sharp fa-solid fa-layer-group" />
									<select
										value={area}
										onChange={(e) => setArea(e.target.value)}>
										<option value="">Area</option>
										{offeredAreas.map((a) => (
											<option key={a} value={a}>
												{areaMeta(a).label}
											</option>
										))}
									</select>
								</label>
								{offeredPeople.map((p) => (
									<button
										key={p.uid}
										type="button"
										className={`wt-smartchip${assignees.has(p.uid) ? '' : ' wt-smartchip--dashed'}`}
										onClick={() => toggleAssignee(p.uid)}>
										<Avatar uid={p.uid} size={24} name={p.name} />
										{p.name}
									</button>
								))}
								<label className="wt-smartchip">
									<i className="fa-sharp fa-solid fa-calendar" />
									<input
										type="date"
										value={due}
										onChange={(e) => setDue(e.target.value)}
										style={{
											border: 'none',
											background: 'transparent',
											font: 'inherit',
											color: 'inherit',
										}}
									/>
								</label>
								<label className="wt-smartchip">
									<i className="fa-sharp fa-solid fa-flag" />
									<select
										value={priority}
										onChange={(e) => setPriority(e.target.value)}>
										{PRIORITY_ORDER.map((p) => (
											<option key={p} value={p}>
												{p[0].toUpperCase() + p.slice(1)}
											</option>
										))}
									</select>
								</label>
							</div>
							<button
								type="button"
								className="wt-subadd"
								onClick={() => setExpanded(true)}>
								<i className="fa-sharp fa-solid fa-sliders" />
								More options
							</button>
						</>
					) : (
						<FullFields
							description={description}
							setDescription={setDescription}
							area={area}
							setArea={setArea}
							assignees={assignees}
							toggleAssignee={toggleAssignee}
							offeredPeople={offeredPeople}
							offeredGates={offeredGates}
							offeredAreas={offeredAreas}
							priority={priority}
							setPriority={setPriority}
							status={status}
							setStatus={setStatus}
							effort={effort}
							setEffort={setEffort}
							projectId={projectId}
							setProjectId={setProjectId}
							projects={projects}
							showProject={fields.project}
							due={due}
							setDue={setDue}
							gate={gate}
							setGate={setGate}
							editing={editing}
							subtasks={subtasks}
							setSubtasks={setSubtasks}
							subInput={subInput}
							setSubInput={setSubInput}
						/>
					)}
				</div>

				<div className="wt-sheet__foot">
					{confirmingDelete ? (
						<div className="wt-delconfirm">
							<span className="wt-delconfirm__msg">
								Delete <strong>“{initial?.title || 'this task'}”</strong>?
								This can’t be undone.
							</span>
							<div className="wt-delconfirm__actions">
								<button
									type="button"
									className="btn-cyv-ghost"
									disabled={deleting}
									onClick={() => setConfirmingDelete(false)}>
									Cancel
								</button>
								<button
									type="button"
									className="wt-btn-danger"
									disabled={deleting}
									onClick={handleDelete}>
									{deleting ? 'Deleting…' : 'Delete'}
								</button>
							</div>
						</div>
					) : (
						<>
							{editing && isAdmin && (
								<button
									type="button"
									className="wt-footdelete"
									aria-label="Delete task"
									title="Delete task"
									onClick={() => setConfirmingDelete(true)}>
									<i className="fa-sharp fa-solid fa-trash" />
								</button>
							)}
							<button
								type="button"
								className="btn-cyv-ghost"
								onClick={onClose}>
								Cancel
							</button>
							<button
								type="button"
								className="btn-cyv-teal"
								disabled={!title.trim() || saving}
								onClick={handleSave}>
								{saving ? 'Saving…' : editing ? 'Save changes' : 'Add task'}
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

function Segmented({ options, value, onChange, labels }) {
	return (
		<div className="wt-segctl">
			{options.map((o) => (
				<button
					key={o}
					type="button"
					className={`wt-segctl__btn${value === o ? ' is-active' : ''}`}
					onClick={() => onChange(o)}>
					{labels ? labels(o) : o[0].toUpperCase() + o.slice(1)}
				</button>
			))}
		</div>
	);
}

function FullFields(props) {
	const {
		description, setDescription, area, setArea, assignees, toggleAssignee,
		priority, setPriority, status, setStatus, effort, setEffort,
		projectId, setProjectId, projects, showProject, due, setDue, gate, setGate, editing,
		subtasks, setSubtasks, subInput, setSubInput,
		// Default to the full sets so a direct mount of FullFields is unchanged.
		offeredPeople = PEOPLE, offeredGates = GATE_ORDER,
		/* Passed down for the same reason as the two above: the compact chip row
		   and this expanded layout must offer the SAME set, and the way that
		   breaks is each reading a module-level list on its own. Defaults to the
		   studio order for a direct mount. */
		offeredAreas = AREA_ORDER,
	} = props;
	return (
		<>
			<div className="wt-field">
				<span className="wt-field__label">Description</span>
				<textarea
					className="wt-textarea"
					value={description}
					placeholder="Add detail… (markdown supported)"
					onChange={(e) => setDescription(e.target.value)}
				/>
			</div>

			{editing && (
				<div className="wt-field">
					<span className="wt-field__label">Status</span>
					<select
						className="wt-select"
						value={status}
						onChange={(e) => setStatus(e.target.value)}>
						{STATUS_ORDER.map((s) => (
							<option key={s} value={s}>
								{STATUS_META[s].label}
							</option>
						))}
					</select>
				</div>
			)}

			<div className="wt-field">
				<span className="wt-field__label">Area</span>
				<select
					className="wt-select"
					value={area}
					onChange={(e) => setArea(e.target.value)}>
					<option value="">No area</option>
					{offeredAreas.map((a) => (
						<option key={a} value={a}>
							{areaMeta(a).label}
						</option>
					))}
				</select>
			</div>

			<div className="wt-field">
				<span className="wt-field__label">Assignees</span>
				<div className="wt-smartchips">
					{offeredPeople.map((p) => (
						<button
							key={p.uid}
							type="button"
							className={`wt-smartchip${assignees.has(p.uid) ? '' : ' wt-smartchip--dashed'}`}
							onClick={() => toggleAssignee(p.uid)}>
							<Avatar uid={p.uid} size={24} name={p.name} />
							{p.name}
						</button>
					))}
				</div>
			</div>

			<div className="wt-field">
				<span className="wt-field__label">Priority</span>
				<Segmented
					options={PRIORITY_ORDER}
					value={priority}
					onChange={setPriority}
				/>
			</div>

			<div className="wt-field">
				<span className="wt-field__label">Effort</span>
				<Segmented
					options={['', ...EFFORTS]}
					value={effort}
					onChange={setEffort}
					labels={(o) => (o === '' ? 'None' : o[0].toUpperCase() + o.slice(1))}
				/>
			</div>

			{/* Projects are a studio collection — a private space neither subscribes
			    to them nor offers the field. */}
			{showProject && (
				<div className="wt-field">
					<span className="wt-field__label">Project</span>
					<select
						className="wt-select"
						value={projectId}
						onChange={(e) => setProjectId(e.target.value)}>
						<option value="">No project</option>
						{projects.map((p) => (
							<option key={p.id} value={p.id}>
								{p.title}
							</option>
						))}
					</select>
				</div>
			)}

			<div className="wt-field">
				<span className="wt-field__label">Due</span>
				<input
					className="wt-input"
					type="date"
					value={due}
					onChange={(e) => setDue(e.target.value)}
				/>
			</div>

			<div className="wt-field">
				<span className="wt-field__label">Parked</span>
				<select
					className="wt-select"
					value={gate}
					onChange={(e) => setGate(e.target.value)}>
					<option value="">Active</option>
					{offeredGates.map((g) => (
						<option key={g} value={g}>
							{gateMeta(g).label}
						</option>
					))}
				</select>
			</div>

			{!editing && (
				<div className="wt-field">
					<span className="wt-field__label">Sub-tasks</span>
					{subtasks.map((st, i) => (
						<div key={i} className="wt-file">
							<i className="fa-sharp fa-solid fa-circle" />
							{st}
						</div>
					))}
					<div className="wt-composer" style={{ marginTop: 0 }}>
						<input
							className="wt-input"
							value={subInput}
							placeholder="Add a sub-task…"
							onChange={(e) => setSubInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && subInput.trim()) {
									setSubtasks((s) => [...s, subInput.trim()]);
									setSubInput('');
								}
							}}
						/>
					</div>
				</div>
			)}

			<p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
				Source is set automatically — you don't pick it.
			</p>
		</>
	);
}

export default TaskForm;
